
import { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router';
import { Sparkles } from 'lucide-react';
import {
  Loader2,
  Check,
  Send,
  RefreshCw,
  Info,
  Mail,
  ArrowRight,
} from 'lucide-react';
import { apiPost, apiPatch, apiGet } from '@/lib/api';
import { getLeadGroup, getLeadGroupLeads } from '@/lib/lead-groups';
import { sendDraftRequest, sendBatchStart, sendBatchEnd } from '@/lib/extension-bridge';
import { useExtension } from '@/hooks/useExtension';
import { useToast } from '@/hooks/useToast';
import { BatchSummaryModal } from '@/components/BatchSummaryModal';
import type {
  Lead,
  EmailGenerateResponse,
  DraftPayload,
} from '@hypema/shared';

/* ── Types ── */

interface EmailData extends EmailGenerateResponse {
  draftStatus: 'none' | 'creating' | 'created' | 'failed' | 'skipped' | 'sent';
}

interface LeadEmailEntry {
  lead: Lead;
  email: EmailData | null;
  editedSubject: string | null;
  editedBody: string | null;
  isRegenerating: boolean;
  generationError?: string; // set when batch generation failed for this lead
}

/* ── Helpers ── */

function getSubject(entry: LeadEmailEntry): string {
  return entry.editedSubject ?? entry.email?.subject ?? '';
}

function getBody(entry: LeadEmailEntry): string {
  return entry.editedBody ?? entry.email?.body ?? '';
}

function leadName(lead: Lead): string {
  return [lead.firstName, lead.lastName].filter(Boolean).join(' ') || lead.email;
}

/* ── Email Editor (shared expanded section — Gmail/Outlook style) ── */

/* ── Email Row — Gmail/Outlook/Apple Mail hybrid ── */

function EmailRow({
  entry,
  isSelected,
  isExpanded,
  onToggleSelect,
  onToggleExpand,
  onSubjectChange,
  onBodyChange,
  onCreateDraft,
  onRegenerate,
  onRewriteAll,
  isRewritingAll,
}: {
  entry: LeadEmailEntry;
  isSelected: boolean;
  isExpanded: boolean;
  onToggleSelect: () => void;
  onToggleExpand: () => void;
  onSubjectChange: (v: string) => void;
  onBodyChange: (v: string) => void;
  onCreateDraft: () => void;
  onRegenerate: (feedback?: string) => void;
  onRewriteAll: (feedback?: string) => void;
  isRewritingAll?: boolean;
}) {
  const [feedbackText, setFeedbackText] = useState('');
  const name = leadName(entry.lead);
  const hasEmail = entry.email !== null;
  const subject = getSubject(entry);
  const body = getBody(entry);
  const snippet = body.slice(0, 90).replace(/\n/g, ' ');
  const initial = (entry.lead.firstName?.[0] || entry.lead.email[0] || '?').toUpperCase();
  const isSent = entry.email?.draftStatus === 'sent' || entry.email?.draftStatus === 'created';

  // Status badge
  const statusBadge = entry.email?.draftStatus === 'sent' ? (
    <span className="flex items-center gap-1 text-[10px] text-green-500 font-medium bg-green-500/10 px-2 py-0.5 rounded-full"><Check size={9} /> Sent</span>
  ) : entry.email?.draftStatus === 'created' ? (
    <span className="flex items-center gap-1 text-[10px] text-blue-500 font-medium bg-blue-500/10 px-2 py-0.5 rounded-full"><Check size={9} /> Drafted</span>
  ) : entry.email?.draftStatus === 'creating' ? (
    <span className="flex items-center gap-1 text-[10px] text-primary font-medium"><Loader2 size={9} className="animate-spin" /> Creating...</span>
  ) : entry.email?.draftStatus === 'failed' ? (
    <span className="text-[10px] text-red-500 font-medium bg-red-500/10 px-2 py-0.5 rounded-full">Failed</span>
  ) : null;

  return (
    <div className={`group transition-colors ${isExpanded ? '' : 'hover:bg-base-200/20'}`}>
      {/* ── Collapsed row — Gmail inbox style ── */}
      <div className="flex items-center gap-3 px-4 py-3 cursor-pointer" onClick={onToggleExpand}>
        <input
          type="checkbox"
          className="checkbox checkbox-sm checkbox-primary shrink-0"
          checked={isSelected}
          onChange={(e) => { e.stopPropagation(); onToggleSelect(); }}
          onClick={(e) => e.stopPropagation()}
        />

        {/* Avatar */}
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
          isSent ? 'bg-green-500/15 text-green-600' : 'bg-primary/15 text-primary'
        }`}>
          {initial}
        </div>

        {/* Sender name — fixed width, bold like Gmail unread */}
        <div className="w-36 shrink-0 min-w-0">
          <p className={`text-sm overflow-hidden whitespace-nowrap ${hasEmail && !isSent ? 'font-semibold' : 'text-base-content/60'}`}>{name}</p>
        </div>

        {/* Vertical separator */}
        <div className="w-px h-5 bg-base-content/10 shrink-0" />

        {/* Subject — medium weight, then snippet in lighter color (Gmail style) */}
        <div className="flex-1 min-w-0 overflow-hidden whitespace-nowrap">
          {hasEmail ? (
            <span>
              <span className={`text-sm ${!isSent ? 'font-medium' : 'text-base-content/60'}`}>{subject}</span>
              <span className="text-sm text-base-content/30"> — {snippet}</span>
            </span>
          ) : entry.generationError ? (
            <span className="text-sm text-error/70 italic">Generation failed — click to retry</span>
          ) : (
            <span className="text-sm text-base-content/25 italic">No email generated</span>
          )}
        </div>

        {/* Status + actions — right side */}
        <div className="flex items-center gap-2 shrink-0">
          {statusBadge}
          {entry.generationError && !hasEmail && (
            entry.isRegenerating ? (
              <span className="flex items-center gap-1 text-[10px] text-primary font-medium">
                <Loader2 size={11} className="animate-spin" /> Generating...
              </span>
            ) : (
              <button
                onClick={(e) => { e.stopPropagation(); onRegenerate(); }}
                className="flex items-center gap-1 text-[10px] text-primary font-medium bg-primary/10 px-2 py-0.5 rounded-full hover:bg-primary/20 transition-colors"
              >
                <RefreshCw size={9} /> Retry
              </button>
            )
          )}
          {hasEmail && (
            <button
              onClick={(e) => { e.stopPropagation(); onRegenerate(); }}
              className="opacity-0 group-hover:opacity-100 transition-opacity btn btn-ghost btn-xs btn-circle"
              title="Regenerate"
            >
              <RefreshCw size={11} className={entry.isRegenerating ? 'animate-spin text-primary' : 'text-base-content/30'} />
            </button>
          )}
        </div>
      </div>

      {/* ── Expanded email view — email client reading pane ── */}
      {isExpanded && entry.email && (
        <div className="mx-4 mb-4 rounded-xl border border-base-300/30 overflow-hidden animate-fade-in shadow-sm">
          {/* Header */}
          <div className="bg-base-200/60 px-6 py-4 border-b border-base-300/25">
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
                isSent ? 'bg-green-500/20 text-green-600' : 'bg-primary/20 text-primary'
              }`}>
                {initial}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold">{name}</span>
                  {entry.lead.company && <span className="text-xs text-base-content/50 font-medium">· {entry.lead.company}</span>}
                  {statusBadge && <span className="ml-auto">{statusBadge}</span>}
                </div>
                <p className="text-[11px] text-base-content/35 mt-0.5">To: {entry.lead.email}</p>
              </div>
            </div>
          </div>

          {/* Subject — colored accent background */}
          <div className="px-6 py-3.5 bg-primary/[0.04] border-b border-base-300/20">
            <input
              value={getSubject(entry)}
              onChange={(e) => onSubjectChange(e.target.value)}
              className="w-full text-base font-semibold bg-transparent outline-none placeholder:text-base-content/25"
              placeholder="Subject line"
            />
          </div>

          {/* Body */}
          <div className="px-6 py-5">
            <textarea
              value={getBody(entry)}
              onChange={(e) => onBodyChange(e.target.value)}
              ref={(el) => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
              onInput={(e) => { const t = e.target as HTMLTextAreaElement; t.style.height = 'auto'; t.style.height = t.scrollHeight + 'px'; }}
              className="w-full text-sm leading-[1.8] min-h-[120px] resize-y bg-transparent outline-none text-base-content/80"
            />
          </div>

          {/* Action bar — feedback + rewrite buttons + send in one row */}
          <div className="px-6 py-3 border-t-2 border-base-300/40 bg-base-200/60 flex items-center gap-3">
            <input
              type="text"
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              placeholder="Anything you'd like to edit?"
              className="flex-1 min-w-0 bg-base-100 border border-base-300/30 rounded-lg px-3 py-1.5 text-sm outline-none placeholder:text-base-content/30 focus:border-primary/40 transition-colors"
              onKeyDown={(e) => { if (e.key === 'Enter' && !entry.isRegenerating) { e.stopPropagation(); onRegenerate(feedbackText.trim() || undefined); setFeedbackText(''); } }}
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={(e) => { e.stopPropagation(); onRewriteAll(feedbackText.trim() || undefined); setFeedbackText(''); }}
              disabled={isRewritingAll}
              className="w-[136px] h-[34px] rounded-lg border border-base-300/30 bg-base-100 flex items-center justify-center gap-1.5 text-xs text-base-content/50 hover:text-primary hover:border-primary/40 transition-colors shrink-0"
            >
              {isRewritingAll ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
              Rewrite All Drafts
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onRegenerate(feedbackText.trim() || undefined); setFeedbackText(''); }}
              disabled={entry.isRegenerating}
              className="w-[136px] h-[34px] rounded-lg border border-base-300/30 bg-base-100 flex items-center justify-center gap-1.5 text-xs text-base-content/50 hover:text-primary hover:border-primary/40 transition-colors shrink-0"
            >
              {entry.isRegenerating ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
              Rewrite This Draft
            </button>
            {entry.email.draftStatus === 'created' || entry.email.draftStatus === 'sent' ? (
              <span className="w-[136px] h-[34px] rounded-lg bg-green-500/10 flex items-center justify-center gap-1.5 text-xs text-green-500 font-medium shrink-0"><Check size={12} /> Sent via Gmail</span>
            ) : entry.email.draftStatus === 'creating' ? (
              <span className="w-[136px] h-[34px] rounded-lg bg-primary/10 flex items-center justify-center gap-1.5 text-xs text-primary shrink-0"><Loader2 size={12} className="animate-spin" /> Sending via Gmail...</span>
            ) : (
              <button onClick={onCreateDraft} className="w-[136px] h-[34px] rounded-lg bg-gradient-to-r from-primary to-purple-500 text-white flex items-center justify-center gap-1.5 text-xs font-semibold shadow-sm hover:shadow-md hover:shadow-primary/20 transition-all shrink-0">
                <Send size={12} /> Send via Gmail
              </button>
            )}
          </div>
        </div>
      )}
      {isExpanded && !entry.email && (
        <div className="mx-4 mb-4 rounded-xl border border-base-300/20 p-6 text-center">
          {entry.generationError ? (
            <>
              <p className="text-xs text-error font-medium">Generation failed</p>
              <p className="text-[11px] text-base-content/30 mt-1">{entry.generationError}</p>
              <button onClick={() => onRegenerate()} className="btn btn-ghost btn-xs text-primary mt-2 gap-1">
                <RefreshCw size={11} /> Retry
              </button>
            </>
          ) : (
            <p className="text-xs text-base-content/30">No email generated yet.</p>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Main Page ── */

export function ReviewSendPage() {
  const [groupId] = useState<string | null>(() => sessionStorage.getItem('selectedGroupId'));
  const toast = useToast();
  const navigate = useNavigate();
  const { connected } = useExtension();

  /* ── State ── */
  const [tenant, setTenant] = useState<any>(null);
  const [groupName, setGroupName] = useState('');
  const [entries, setEntries] = useState<Record<string, LeadEmailEntry>>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [retryingAll, setRetryingAll] = useState(false);
  const [sendProgress, setSendProgress] = useState({ done: 0, total: 0, sent: 0, skipped: 0, failed: 0 });
  const cancelRef = useRef(false);
  const [batchSummary, setBatchSummary] = useState<{ created: number; skipped: number; failed: number; total: number } | null>(null);
  const [feedbackHistory, setFeedbackHistory] = useState<Record<string, string[]>>({});
  const [rewritingAll, setRewritingAll] = useState(false);

  /* ── On mount: load leads + existing emails ── */
  useEffect(() => {
    if (!groupId) return;
    let cancelled = false;
    async function init() {
      setReviewLoading(true);
      try {
        const generatedIdsRaw = sessionStorage.getItem('generatedLeadIds');
        const generatedIds: string[] | null = generatedIdsRaw ? JSON.parse(generatedIdsRaw) : null;

        // Check for instant preview data passed from DraftEmailsPage
        const previewDataRaw = sessionStorage.getItem('previewEmailData');
        const previewData: Record<string, { subject: string; body: string }> | null =
          previewDataRaw ? JSON.parse(previewDataRaw) : null;
        // Clean up one-time preview data
        if (previewDataRaw) sessionStorage.removeItem('previewEmailData');

        // Load feedback history carried over from DraftEmailsPage
        const draftFeedbackRaw = sessionStorage.getItem('draftFeedbackHistory');
        const draftFeedback: string[] | null = draftFeedbackRaw ? JSON.parse(draftFeedbackRaw) : null;
        if (draftFeedbackRaw) sessionStorage.removeItem('draftFeedbackHistory');

        // Load failed leads info from batch generation
        const failedLeadsRaw = sessionStorage.getItem('failedLeads');
        const failedLeadsMap = new Map<string, string>();
        if (failedLeadsRaw) {
          const failedLeads: { leadId: string; error: string }[] = JSON.parse(failedLeadsRaw);
          for (const f of failedLeads) failedLeadsMap.set(f.leadId, f.error);
          sessionStorage.removeItem('failedLeads');
        }

        const [group, allLeads, tenantData, existingEmails] = await Promise.all([
          getLeadGroup(groupId!),
          getLeadGroupLeads(groupId!) as Promise<Lead[]>,
          apiGet<any>('/tenants/current'),
          generatedIds && generatedIds.length > 0
            ? apiPost<Record<string, { id: string; subject: string; body: string; unsubscribeUrl: string }>>(
                '/emails/by-leads',
                { leadIds: generatedIds }
              ).catch(() => null)
            : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setGroupName(group.name);
        setTenant(tenantData);

        const leads = generatedIds ? allLeads.filter((l) => generatedIds.includes(l.id)) : allLeads;

        const record: Record<string, LeadEmailEntry> = {};
        for (const lead of leads) {
          // Use instant preview data if available, fall back to null
          const preview = previewData?.[lead.id];
          record[lead.id] = {
            lead,
            email: preview ? { id: '', subject: preview.subject, body: preview.body, unsubscribeUrl: '', draftStatus: 'none' } : null,
            editedSubject: null,
            editedBody: null,
            isRegenerating: false,
            generationError: failedLeadsMap.get(lead.id),
          };
        }

        // Show preview data immediately so user sees content right away
        if (previewData && !cancelled) {
          setEntries({ ...record });
          setSelected(new Set(leads.filter((l) => l.status !== 'sent').map((l) => l.id)));
          setReviewLoading(false);
        }

        // Merge in DB data (has real IDs, unsubscribeUrl, etc.)
        const emailData = existingEmails ?? (
          leads.length > 0
            ? await apiPost<Record<string, { id: string; subject: string; body: string; unsubscribeUrl: string }>>(
                '/emails/by-leads',
                { leadIds: leads.map((l) => l.id) }
              ).catch(() => null)
            : null
        );

        if (emailData) {
          for (const [leadId, email] of Object.entries(emailData)) {
            if (record[leadId]) {
              const leadStatus = record[leadId].lead.status;
              record[leadId].email = {
                id: email.id,
                subject: email.subject,
                body: email.body,
                unsubscribeUrl: email.unsubscribeUrl,
                draftStatus: leadStatus === 'sent' ? 'sent' : 'none',
              };
            }
          }
        }

        if (!cancelled) {
          setEntries(record);
          setSelected(new Set(leads.filter((l) => l.status !== 'sent').map((l) => l.id)));
          // Initialize per-lead feedback history from DraftEmailsPage's accumulated feedback
          if (draftFeedback && draftFeedback.length > 0) {
            const initialFeedback: Record<string, string[]> = {};
            for (const lead of leads) {
              initialFeedback[lead.id] = [...draftFeedback];
            }
            setFeedbackHistory(initialFeedback);
          }
        }
      } catch (err: any) {
        if (!cancelled) toast.error(err.message || 'Failed to load leads');
      }
      if (!cancelled) setReviewLoading(false);
    }
    init();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  /* ── Review helpers ── */

  const updateEntry = useCallback((leadId: string, patch: Partial<LeadEmailEntry>) => {
    setEntries((prev) => ({
      ...prev,
      [leadId]: { ...prev[leadId], ...patch },
    }));
  }, []);

  const allLeads = Object.values(entries);
  const selectedCount = selected.size;
  const totalCount = allLeads.length;
  const hasAnyEmails = allLeads.some((e) => e.email !== null);
  const failedLeadIds = allLeads.filter((e) => !e.email && (e.generationError || e.lead.status === 'new')).map((e) => e.lead.id);
  const failedCount = failedLeadIds.length;
  const sendableCount = allLeads.filter((e) => selected.has(e.lead.id) && e.email && e.email.draftStatus !== 'created' && e.email.draftStatus !== 'sent' && e.email.draftStatus !== 'skipped').length;

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    if (selected.size === totalCount) setSelected(new Set());
    else setSelected(new Set(allLeads.map((e) => e.lead.id)));
  }

  async function handleRegenerateSingle(leadId: string, feedback?: string) {
    updateEntry(leadId, { isRegenerating: true });
    // Build cumulative feedback history for this lead
    const history = [...(feedbackHistory[leadId] || [])];
    if (feedback) {
      history.push(feedback);
      setFeedbackHistory((prev) => ({ ...prev, [leadId]: history }));
    }
    try {
      const topicGoalText = tenant?.topicGoalText || sessionStorage.getItem('outreach_last_goal') || 'Generate a professional outreach email';
      const email = await apiPost<EmailGenerateResponse>('/emails/generate', {
        leadId,
        topicGoalText,
        feedbackHistory: history.length > 0 ? history : undefined,
      });
      updateEntry(leadId, {
        email: { ...email, draftStatus: 'none' },
        editedSubject: null,
        editedBody: null,
        isRegenerating: false,
      });
      toast.success('Email regenerated');
    } catch (err: any) {
      updateEntry(leadId, { isRegenerating: false });
      toast.error(err.message || 'Regeneration failed');
    }
  }

  async function handleRetryAllFailed() {
    if (failedCount === 0) return;
    setRetryingAll(true);
    // Mark all failed leads as regenerating
    for (const id of failedLeadIds) updateEntry(id, { isRegenerating: true });
    const topicGoalText = tenant?.topicGoalText || sessionStorage.getItem('outreach_last_goal') || 'Generate a professional outreach email';

    // Retry all in parallel
    const results = await Promise.allSettled(
      failedLeadIds.map((id) =>
        apiPost<EmailGenerateResponse>('/emails/generate', { leadId: id, topicGoalText })
          .then((email) => ({ id, email }))
      ),
    );

    let succeeded = 0;
    let stillFailed = 0;
    for (const result of results) {
      if (result.status === 'fulfilled') {
        const { id, email } = result.value;
        updateEntry(id, {
          email: { ...email, draftStatus: 'none' },
          editedSubject: null,
          editedBody: null,
          isRegenerating: false,
          generationError: undefined,
        });
        succeeded++;
      } else {
        stillFailed++;
      }
    }
    // Clear regenerating state for any that failed
    for (const id of failedLeadIds) {
      const entry = allLeads.find((e) => e.lead.id === id);
      if (entry?.isRegenerating) updateEntry(id, { isRegenerating: false });
    }
    setRetryingAll(false);
    if (succeeded > 0) toast.success(`${succeeded} email(s) generated successfully`);
    if (stillFailed > 0) toast.error(`${stillFailed} email(s) still failed`);
  }

  async function handleRewriteAll(feedback?: string) {
    setRewritingAll(true);
    const topicGoalText = tenant?.topicGoalText || sessionStorage.getItem('outreach_last_goal') || 'Generate a professional outreach email';
    const leadIds = Object.keys(entries).filter((id) => entries[id].email);
    // Mark all as regenerating
    for (const id of leadIds) updateEntry(id, { isRegenerating: true });

    // Add feedback to all histories
    if (feedback) {
      setFeedbackHistory((prev) => {
        const next = { ...prev };
        for (const id of leadIds) next[id] = [...(next[id] || []), feedback];
        return next;
      });
    }

    // Regenerate in parallel batches of 100
    const BATCH_SIZE = 100;
    let succeeded = 0;
    let failed = 0;
    for (let i = 0; i < leadIds.length; i += BATCH_SIZE) {
      const batch = leadIds.slice(i, i + BATCH_SIZE);
      const results = await Promise.allSettled(
        batch.map(async (leadId) => {
          const history = [...(feedbackHistory[leadId] || []), ...(feedback ? [feedback] : [])];
          const email = await apiPost<EmailGenerateResponse>('/emails/generate', {
            leadId,
            topicGoalText,
            feedbackHistory: history.length > 0 ? history : undefined,
          });
          updateEntry(leadId, {
            email: { ...email, draftStatus: 'none' },
            editedSubject: null, editedBody: null, isRegenerating: false,
          });
          return email;
        })
      );
      for (const r of results) {
        if (r.status === 'fulfilled') succeeded++;
        else failed++;
      }
    }
    // Clean up any still-regenerating entries
    for (const id of leadIds) updateEntry(id, { isRegenerating: false });
    setRewritingAll(false);
    toast.success(`Rewrote ${succeeded} email${succeeded !== 1 ? 's' : ''}${failed > 0 ? `, ${failed} failed` : ''}`);
  }

  async function handleCreateDraft(leadId: string) {
    const entry = entries[leadId];
    if (!entry?.email) return;

    if (!connected) {
      toast.error('Chrome extension not detected. Install it to create drafts.');
      return;
    }

    updateEntry(leadId, { email: { ...entry.email, draftStatus: 'creating' } });

    try {
      // Start a mini-batch of 1 so the extension's service worker accepts the request
      sendBatchStart(1);
      await new Promise((r) => setTimeout(r, 500));

      const hasEdits = entry.editedSubject !== null || entry.editedBody !== null;
      let payload: DraftPayload;

      if (hasEdits) {
        payload = {
          provider: 'gmail',
          to: entry.lead.email,
          subject: getSubject(entry),
          body: getBody(entry),
          leadId: entry.lead.id,
          emailId: entry.email.id,
        };
      } else {
        payload = await apiPost<DraftPayload>('/emails/drafts/payload', {
          leadId: entry.lead.id,
          generatedEmailId: entry.email.id,
        });
        // Ensure leadId/emailId are always on the payload for reply tracking
        payload.leadId = entry.lead.id;
        payload.emailId = entry.email.id;
      }

      console.log('[HyPeMa Debug] Sending draft payload:', JSON.stringify(payload));
      const result = await sendDraftRequest(payload);
      console.log('[HyPeMa Debug] Draft result:', JSON.stringify(result));
      if (result.success) {
        if (result.sent) {
          updateEntry(leadId, { email: { ...entry.email, draftStatus: 'sent' } });
          apiPatch(`/leads/${leadId}`, { status: 'sent' }).catch(() => toast.error('Failed to update sent status'));
          toast.success(`Email sent to ${entry.lead.email}`);
        } else {
          updateEntry(leadId, { email: { ...entry.email, draftStatus: 'skipped' } });
          toast.info(`Skipped ${entry.lead.email}`);
        }
      } else {
        updateEntry(leadId, { email: { ...entry.email, draftStatus: 'failed' } });
        toast.error(result.error || 'Failed to create draft');
      }
    } catch (err: any) {
      console.error('[HyPeMa Debug] Draft exception:', err);
      updateEntry(leadId, { email: { ...entry.email, draftStatus: 'failed' } });
      toast.error(err.message || 'Draft creation failed');
    }
  }

  async function handleSendAll() {
    if (!connected) {
      toast.error('Chrome extension not detected. Install it to create drafts.');
      return;
    }

    const sendable = allLeads.filter(
      (e) => selected.has(e.lead.id) && e.email && e.email.draftStatus !== 'created' && e.email.draftStatus !== 'skipped' && e.email.draftStatus !== 'sent'
    );
    if (sendable.length === 0) {
      toast.warning('No selected emails to send');
      return;
    }

    cancelRef.current = false;
    setSending(true);
    setSendProgress({ done: 0, total: sendable.length, sent: 0, skipped: 0, failed: 0 });
    sendBatchStart(sendable.length);

    // Prefetch all draft payloads in parallel while extension processes BATCH_START
    const payloadPromises = sendable.map((entry) => {
      const hasEdits = entry.editedSubject !== null || entry.editedBody !== null;
      if (hasEdits) {
        return Promise.resolve<DraftPayload>({
          provider: 'gmail',
          to: entry.lead.email,
          subject: getSubject(entry),
          body: getBody(entry),
          leadId: entry.lead.id,
          emailId: entry.email!.id,
        });
      }
      return apiPost<DraftPayload>('/emails/drafts/payload', {
        leadId: entry.lead.id,
        generatedEmailId: entry.email!.id,
      }).then((p) => ({ ...p, leadId: entry.lead.id, emailId: entry.email!.id }))
        .catch(() => null);
    });
    // Wait for extension BATCH_START processing AND all payloads in parallel
    const [payloads] = await Promise.all([
      Promise.all(payloadPromises),
      new Promise((r) => setTimeout(r, 150)),
    ]);

    let sent = 0;
    let skipped = 0;
    let failed = 0;

    for (let i = 0; i < sendable.length; i++) {
      if (cancelRef.current) {
        for (let j = i; j < sendable.length; j++) {
          skipped++;
          updateEntry(sendable[j].lead.id, { email: { ...sendable[j].email!, draftStatus: 'skipped' } });
        }
        break;
      }

      const entry = sendable[i];
      const payload = payloads[i];

      if (!payload) {
        failed++;
        updateEntry(entry.lead.id, { email: { ...entry.email!, draftStatus: 'failed' } });
        setSendProgress({ done: i + 1, total: sendable.length, sent, skipped, failed });
        continue;
      }

      try {
        updateEntry(entry.lead.id, { email: { ...entry.email!, draftStatus: 'creating' } });
        const result = await sendDraftRequest(payload);

        if (result.success) {
          if (result.sent) {
            sent++;
            updateEntry(entry.lead.id, { email: { ...entry.email!, draftStatus: 'sent' } });
            apiPatch(`/leads/${entry.lead.id}`, { status: 'sent' }).catch(() => toast.error('Failed to update sent status'));
          } else {
            skipped++;
            updateEntry(entry.lead.id, { email: { ...entry.email!, draftStatus: 'skipped' } });
          }
        } else {
          if (result.error?.includes('cancelled') || result.error?.includes('Batch')) {
            skipped++;
            updateEntry(entry.lead.id, { email: { ...entry.email!, draftStatus: 'skipped' } });
            for (let j = i + 1; j < sendable.length; j++) {
              skipped++;
              updateEntry(sendable[j].lead.id, { email: { ...sendable[j].email!, draftStatus: 'skipped' } });
            }
            break;
          }
          failed++;
          updateEntry(entry.lead.id, { email: { ...entry.email!, draftStatus: 'failed' } });
        }
      } catch {
        failed++;
        updateEntry(entry.lead.id, { email: { ...entry.email!, draftStatus: 'failed' } });
      }

      setSendProgress({ done: i + 1, total: sendable.length, sent, skipped, failed });
    }

    sendBatchEnd({ created: sent, skipped, failed });
    setSending(false);
    setBatchSummary({ created: sent, skipped, failed, total: sendable.length });
  }

  function toggleExpand(id: string) {
    setExpandedId((prev) => (prev === id ? null : id));
  }

  /* ── Render ── */

  // Empty state: only show when we truly have no context (no group, no data)
  const draftsGenerated = typeof window !== 'undefined' ? sessionStorage.getItem('draftsGenerated') === 'true' : false;
  const hasEntries = Object.keys(entries).length > 0;
  if (!groupId && !hasEntries && !reviewLoading) {
    return (
      <div className="flex flex-col gap-[50px] max-w-5xl mx-auto w-full animate-fade-in">
        <div className="flex justify-center">
          <Link to="/draft-emails"
            className="w-[220px] h-[68px] flex items-center justify-center rounded-xl text-xs font-semibold text-white bg-gradient-to-r from-primary to-purple-500 shadow-md hover:shadow-lg hover:shadow-primary/30 hover:scale-[1.02] transition-all text-center px-3 gap-1.5 whitespace-nowrap">
            <Sparkles size={14} className="shrink-0" /> First Draft Emails in Step 2
          </Link>
        </div>
        <div>
          <h1 className="text-xl font-bold tracking-tight">Review & Send</h1>
          <p className="text-sm text-base-content/40 mt-1">Draft emails in Step 2 first, then review and send them here.</p>
        </div>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="skeleton h-8 w-28 rounded-lg" />
            <div className="skeleton h-8 w-20 rounded-lg" />
            <div className="flex-1" />
            <div className="skeleton h-8 w-32 rounded-lg" />
          </div>
          {[...Array(5)].map((_, i) => (
            <div key={i} className="glass-card rounded-xl p-4 flex items-center gap-4">
              <div className="skeleton w-10 h-10 rounded-full shrink-0" />
              <div className="flex-1 space-y-2"><div className="skeleton h-4 w-40 rounded" /><div className="skeleton h-3 w-64 rounded" /></div>
              <div className="skeleton h-6 w-16 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const sentCount = allLeads.filter((e) => e.email?.draftStatus === 'sent').length;
  const skippedCount = allLeads.filter((e) => e.email?.draftStatus === 'skipped').length;
  const doneCount = sentCount + skippedCount;

  return (
    <div className="flex flex-col gap-[50px] max-w-7xl mx-auto w-full animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-xl font-bold tracking-tight">Review & Send</h1>
        <p className="text-sm text-base-content/40 mt-1">
          {groupName ? `${groupName} · ` : ''}Review emails, edit inline, then create Gmail drafts.
        </p>
      </div>

      {/* Done banner */}
      {doneCount > 0 && doneCount >= allLeads.length && !sending && (
        <div className="glass-card rounded-2xl p-8 border border-green-500/20 bg-green-500/5 text-center space-y-4">
          <div className="w-16 h-16 rounded-2xl bg-green-500/10 flex items-center justify-center mx-auto">
            <Check size={32} className="text-green-500" />
          </div>
          <div>
            <p className="text-xl font-bold text-green-600">All Done!</p>
            <p className="text-sm text-base-content/50 mt-1">
              {sentCount} email{sentCount !== 1 ? 's' : ''} sent successfully
              {skippedCount > 0 ? ` · ${skippedCount} skipped` : ''}
            </p>
          </div>
          <div className="flex items-center justify-center pt-2">
            <button
              onClick={() => {
                sessionStorage.removeItem('selectedGroupId');
                sessionStorage.removeItem('draftsGenerated');
                sessionStorage.removeItem('generatedLeadIds');
                navigate('/leads');
              }}
              className="w-[220px] h-[68px] rounded-xl bg-gradient-to-r from-primary to-purple-500 text-white shadow-md hover:shadow-lg hover:shadow-primary/30 hover:scale-[1.02] transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              <ArrowRight size={18} />
              <span className="text-sm font-semibold">Start New Outreach</span>
            </button>
          </div>
        </div>
      )}

      {/* Email inbox — Gmail/Outlook/Apple Mail hybrid */}
      <div className="glass-card rounded-xl overflow-hidden">
        {/* Toolbar — clean, Outlook-style */}
        <div className="px-4 py-3 border-b border-base-300/10 flex items-center justify-between">
          {reviewLoading ? (
            <span className="text-sm text-base-content/40">Loading emails...</span>
          ) : (
          <label className="flex items-center gap-2.5 text-sm cursor-pointer">
            <input
              type="checkbox"
              className="checkbox checkbox-sm checkbox-primary"
              checked={selectedCount === totalCount && totalCount > 0}
              onChange={toggleAll}
            />
            <span className="text-base-content/60">{selectedCount} of {totalCount} selected</span>
          </label>
          )}
          <div className="flex items-center gap-2">
          {failedCount > 0 && (
            <button
              onClick={handleRetryAllFailed}
              disabled={retryingAll}
              className="btn btn-ghost btn-sm gap-1.5 text-error"
            >
              {retryingAll ? (
                <><Loader2 size={14} className="animate-spin" /> Retrying...</>
              ) : (
                <><RefreshCw size={14} /> Retry Failed ({failedCount})</>
              )}
            </button>
          )}
          {hasAnyEmails && (
            <button
              onClick={handleSendAll}
              disabled={sendableCount === 0 || sending}
              className="btn btn-primary btn-sm gap-1.5"
            >
              {sending ? (
                <><Loader2 size={14} className="animate-spin" /> Sending via Gmail {sendProgress.done}/{sendProgress.total}</>
              ) : (
                <><Send size={14} /> Send All via Gmail ({sendableCount})</>
              )}
            </button>
          )}
          </div>
        </div>

        {/* Email list */}
        {reviewLoading ? (
          <div className="p-6 space-y-2">
            {[...Array(5)].map((_, i) => <div key={i} className="h-12 animate-shimmer rounded" />)}
          </div>
        ) : totalCount === 0 ? (
          <div className="p-8 text-center text-base-content/40 text-sm">
            No leads found.
          </div>
        ) : (
          <div className="divide-y divide-base-300/10">
            {allLeads.map((entry) => (
              <EmailRow
                key={entry.lead.id}
                entry={entry}
                isSelected={selected.has(entry.lead.id)}
                isExpanded={expandedId === entry.lead.id}
                onToggleSelect={() => toggleSelect(entry.lead.id)}
                onToggleExpand={() => toggleExpand(entry.lead.id)}
                onSubjectChange={(v) => updateEntry(entry.lead.id, { editedSubject: v })}
                onBodyChange={(v) => updateEntry(entry.lead.id, { editedBody: v })}
                onCreateDraft={() => handleCreateDraft(entry.lead.id)}
                onRegenerate={(feedback) => handleRegenerateSingle(entry.lead.id, feedback)}
                onRewriteAll={(feedback) => handleRewriteAll(feedback)}
                isRewritingAll={rewritingAll}
              />
            ))}
          </div>
        )}
      </div>


      {/* Sending overlay */}
      {sending && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-base-100/95" />
          <div className="relative bg-base-100 border border-base-300/30 rounded-2xl p-6 w-full max-w-sm mx-4 space-y-5 animate-fade-in shadow-2xl">
            <div className="text-center space-y-2">
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mx-auto">
                <Send size={24} className="text-primary animate-pulse" />
              </div>
              <h2 className="text-lg font-bold">Sending Emails</h2>
              <p className="text-sm text-base-content/40">
                Switch to Gmail to review and send each draft.
              </p>
            </div>

            <div className="space-y-2">
              <div className="h-2 bg-base-content/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all duration-500"
                  style={{ width: `${sendProgress.total > 0 ? (sendProgress.done / sendProgress.total) * 100 : 0}%` }}
                />
              </div>
              <p className="text-sm font-semibold text-primary text-center">
                {sendProgress.done}/{sendProgress.total}
              </p>
            </div>

            <div className="space-y-2">
              {sendProgress.sent > 0 && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-success/5 border border-success/10">
                  <Check size={16} className="text-success shrink-0" />
                  <span className="text-sm font-medium flex-1">Sent</span>
                  <span className="text-sm font-bold text-success">{sendProgress.sent}</span>
                </div>
              )}
              {sendProgress.skipped > 0 && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-warning/5 border border-warning/10">
                  <Info size={16} className="text-warning shrink-0" />
                  <span className="text-sm font-medium flex-1">Skipped</span>
                  <span className="text-sm font-bold text-warning">{sendProgress.skipped}</span>
                </div>
              )}
              {sendProgress.failed > 0 && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-error/5 border border-error/10">
                  <Info size={16} className="text-error shrink-0" />
                  <span className="text-sm font-medium flex-1">Failed</span>
                  <span className="text-sm font-bold text-error">{sendProgress.failed}</span>
                </div>
              )}
            </div>

            <button
              onClick={() => {
                cancelRef.current = true;
                sendBatchEnd({ created: sendProgress.sent, skipped: sendProgress.skipped, failed: sendProgress.failed });
                setSending(false);
                setBatchSummary({ created: sendProgress.sent, skipped: sendProgress.skipped + (sendProgress.total - sendProgress.done), failed: sendProgress.failed, total: sendProgress.total });
              }}
              className="btn btn-ghost btn-sm btn-block text-base-content/40 hover:text-error"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Batch summary modal */}
      <BatchSummaryModal
        open={!!batchSummary}
        created={batchSummary?.created ?? 0}
        skipped={batchSummary?.skipped ?? 0}
        failed={batchSummary?.failed ?? 0}
        total={batchSummary?.total ?? 0}
        onClose={() => setBatchSummary(null)}
      />
    </div>
  );
}
